import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import type { Profile } from '../../api'
import type { Destination } from '../types'
import { LogoSlot } from '../ui'
import { Button, Icon, type IconName } from '../../ui'
import { domainOf } from '../setup'
import { OUTPUT_REASON } from '../adapter'
import { t } from '../../i18n'
import { withMono } from '../../mono'
import { fixKind, retryLeft, rowStatus, type Tip, type TipAction } from './model'
import { useQuery } from '@tanstack/react-query'
import { get, type Ingest } from '../../api'
import { useVerticalKey } from '../VerticalInput'

const HIDDEN_KEY = 'strevix.studio.hiddenTips'

/** Hidden "make it better" tips: this browser only; storage may be unavailable. */
function useHidden(): [string[], (id: string) => void] {
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]') as string[]
    } catch {
      return []
    }
  })
  const hide = (id: string) =>
    setHidden((h) => {
      const next = [...h, id]
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify(next))
      } catch {
        /* the tip stays hidden for this page only */
      }
      return next
    })
  return [hidden, hide]
}

/**
 * Paste the stream key of one output (needs_key or platform_rejected_key):
 * PATCH /api/destinations/{id} {key}. The key never stays in the page after it is sent.
 */
export function KeyFix({ dest, profile, autoFocus, onSave, testId }: {
  dest: Destination; profile?: Profile; autoFocus?: boolean; onSave: (id: number, key: string) => Promise<void>; testId?: string;
}) {
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (autoFocus) input.current?.focus()
  }, [autoFocus])
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const v = key.trim()
    if (!v || busy) return
    if (/\s/.test(v)) return setErr(t('fix.key.spaces'))
    setBusy(true)
    setErr('')
    try {
      await onSave(dest.id, v)
      setKey('')
    } catch (e2) {
      setErr((e2 as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const id = `${testId ?? 'key-fix'}-${dest.id}`
  return (
    <form onSubmit={submit} data-testid={id} aria-label={t('fix.key.form', { name: dest.name })} className="flex flex-col gap-1.5">
      <div className="flex gap-1.5">
        <label htmlFor={`${id}-input`} className="sr-only">{t('fix.key.label', { name: dest.name })}</label>
        <input ref={input} id={`${id}-input`} type="password" autoComplete="off" spellCheck={false} value={key} disabled={busy}
          placeholder={t('fix.key.placeholder', { platform: profile?.name ?? dest.profileName })}
          onChange={(e) => { setKey(e.target.value); setErr('') }}
          className={`sx-input min-w-0 flex-1 text-xs outline-none placeholder:text-muted focus:border-accent focus:shadow-focus max-md:min-h-[44px] ${err ? 'border-fail shadow-[inset_0_0_0_1px_var(--fail)]' : ''}`} />
        <Button variant="primary" type="submit" disabled={!key.trim() || busy} busy={busy} className="max-md:min-h-[44px]">
          {busy ? t('fix.key.saving') : t('fix.key.save')}
        </Button>
      </div>
      {err && <span role="alert" className="inline-flex items-center gap-1 text-[11px] text-fail"><Icon name="state-fail" size={12} />{err}</span>}
    </form>
  )
}

/** The fix of an output that needs the streamer (docs/live-studio.md §3.5): what happened and what can be done now. */
export function FixBlock({ d, profile, now, onSaveKey, onSettings }: {
  d: Destination; profile?: Profile; now: number; onSaveKey: (id: number, key: string) => Promise<void>; onSettings: (id: number) => void;
}) {
  const kind = fixKind(d)
  if (!kind) return null
  const fail = d.state === 'failed'
  const left = retryLeft(d, now)
  const keyUrl = profile?.key_url
  return (
    <div data-testid={`fix-${d.id}`} data-kind={kind}
      className={`flex flex-col gap-2 rounded-md border p-2.5 ${fail ? 'border-[3px] border-double border-fail/45 bg-fail/[.06]' : 'border-dashed border-warn/45 bg-warn/[.06]'}`}>
      <p className={`m-0 text-[13px] ${fail ? 'text-fail' : 'text-warn'}`}>
        <Icon name={fail ? 'state-fail' : 'state-warn'} size={12} className="mr-1.5 inline align-[-1px]" />
        {/* the countdown is ours, ticking: the reason without the adapter's "Retrying in" */}
        {kind === 'retry' && d.reason ? OUTPUT_REASON[d.reason] : d.note}
        {kind === 'retry' && left ? <span data-testid={`fix-retry-${d.id}`} className="tabular font-mono text-muted"> {fail ? t('fix.nextTry', { s: left }) : t('fix.retryIn', { s: left })}</span> : null}
      </p>
      {kind === 'retry' && <p className="m-0 text-xs text-text-soft">{t('fix.retry.body')}</p>}
      {kind === 'key' && (
        <>
          <KeyFix dest={d} profile={profile} onSave={onSaveKey} testId="tip-key" />
          {keyUrl && (
            <a href={keyUrl} target="_blank" rel="noopener noreferrer" data-testid={`fix-key-url-${d.id}`}
              className="inline-flex min-h-8 items-center self-start text-xs text-accent no-underline hover:underline max-md:min-h-[44px]">
              <span className="inline-flex items-center gap-1">{t('fix.key.open', { site: domainOf(keyUrl) || (profile?.name ?? d.profileName) })}<Icon name="external" size={12} /></span>
            </a>
          )}
        </>
      )}
      {kind === 'track' && (
        <Button size="sm" data-testid={`fix-track-${d.id}`} onClick={() => onSettings(d.id)} className="self-start max-md:min-h-[44px]">
          <Icon name="tune" size={16} />{t('row.changeTrack')}
        </Button>
      )}
      {kind === 'settings' && (
        <Button size="sm" onClick={() => onSettings(d.id)} className="self-start max-md:min-h-[44px]">
          <Icon name="tune" size={16} />{t('fix.settings')}
        </Button>
      )}
    </div>
  )
}

const GROUPS: { id: 'now' | 'before' | 'better'; kinds: Tip['kind'][] }[] = [
  { id: 'now', kinds: ['input', 'fix', 'state'] },
  { id: 'before', kinds: ['todo'] },
  { id: 'better', kinds: ['advice', 'ok'] },
]

/** Tips: Now → Before you go live → To make it better (hideable). */
/** The button of a VERT-1 tip: open the vertical setup, turn the vertical input on, copy the server address. */
function TipButton({ action, testId, onVertical }: { action: TipAction; testId: string; onVertical?: () => void }) {
  const v = useVerticalKey()
  const [copied, setCopied] = useState(false)
  const ingest = useQuery({ queryKey: ['ingest'], queryFn: () => get<Ingest>('/api/ingest'), enabled: action === 'copy-server' })
  if (action === 'copy-server') {
    const server = ingest.data?.rtmps ?? ingest.data?.rtmp
    return (
      <Button size="sm" data-testid={testId} disabled={!server} className="w-fit max-md:min-h-[44px]"
        onClick={async () => { if (!server) return; await navigator.clipboard?.writeText(server).catch(() => undefined); setCopied(true); setTimeout(() => setCopied(false), 1500) }}>
        <Icon name={copied ? 'check' : 'copy'} size={16} className={copied ? 'text-live' : undefined} />{t('vert.f.refused.a')}
      </Button>
    )
  }
  if (action === 'vertical-on') {
    return (
      <Button size="sm" data-testid={testId} busy={v.busy === 'on'} disabled={!!v.busy || !v.key} onClick={() => void v.turnOn()} className="w-fit max-md:min-h-[44px]">
        {t('vert.in.turnOn')}
      </Button>
    )
  }
  return <Button size="sm" data-testid={testId} onClick={onVertical} className="w-fit max-md:min-h-[44px]">{t('vert.f.waiting.a')}</Button>
}

export function Tips({ tips, profiles, now, onSaveKey, onSettings, only, onVertical }: {
  tips: Tip[]; profiles?: Profile[]; now: number; onSaveKey: (id: number, key: string) => Promise<void>; onSettings: (id: number) => void;
  /** VERT-1: open the Vertical input settings (the Input panel) */
  onVertical?: () => void;
  /** the guide shows "Before you go live" only */
  only?: 'before';
}) {
  const [hidden, hide] = useHidden()
  const shown = tips.filter((x) => !(x.kind === 'advice' || x.kind === 'ok') || !hidden.includes(x.id))
  const groups = GROUPS.filter((g) => !only || g.id === only).map((g) => ({ ...g, tips: shown.filter((x) => g.kinds.includes(x.kind)) })).filter((g) => g.tips.length)
  if (!groups.length) return <p data-testid="tips-empty" className="m-0 text-[13px] text-muted">{t('tips.empty')}</p>
  return (
    <div data-testid="tips" className="flex flex-col gap-4">
      {groups.map((g) => (
        <section key={g.id} data-testid={`tips-${g.id}`} aria-label={t(`tips.${g.id}`)} className="flex flex-col gap-2">
          <h3 className={`sx-label m-0 inline-flex items-center gap-1.5 ${g.id === 'now' ? 'text-warn' : ''}`}>
            {g.id === 'now' && <Icon name="state-warn" size={12} />}{t(`tips.${g.id}`).toUpperCase()}
          </h3>
          {g.tips.map((x) => {
            if (x.kind === 'fix' && x.dest) {
              const st = rowStatus(x.dest, now)
              return (
                <div key={x.id} data-testid={`tip-${x.id}`} className="flex flex-col gap-2">
                  <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
                    <LogoSlot platform={x.dest.platform} />
                    <span className="min-w-0 flex-1 truncate">{x.dest.name}</span>
                    {x.dest.profileName !== x.dest.name && (
                      <span className={`whitespace-nowrap font-mono text-[10px] font-medium tracking-[.06em] ${st.tone === 'fail' ? 'text-fail' : 'text-warn'}`}>{x.dest.profileName}</span>
                    )}
                  </span>
                  <FixBlock d={x.dest} profile={profiles?.find((p) => p.id === x.dest!.profile)} now={now} onSaveKey={onSaveKey} onSettings={onSettings} />
                </div>
              )
            }
            // the system's tip (ScreenStudioOnAir «Tips», CB-11): frame `line`, the colour only on the icon
            const mark: IconName = x.icon ?? (x.kind === 'input' ? 'state-warn' : x.kind === 'ok' ? 'check' : x.kind === 'todo' || x.kind === 'state' ? 'state-idle' : 'target')
            const markC = x.kind === 'input' && x.icon !== 'lock' ? 'text-warn' : x.kind === 'ok' ? 'text-live' : x.kind === 'todo' || x.kind === 'state' || x.icon ? 'text-muted' : 'text-accent'
            return (
              <div key={x.id} data-testid={`tip-${x.id}`} className="flex gap-2.5 rounded-md border border-line bg-panel p-3">
                <span className={`mt-0.5 flex-none ${markC}`}><Icon name={mark} size={16} /></span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-ui-title font-medium">{x.kind === 'input' ? withMono(x.title) : x.title}</span>
                  {x.body && <span className="text-[13px] leading-[18px] text-text-soft">{x.body}</span>}
                  {x.link?.internal && <Link to={x.link.href} data-testid={`tip-${x.id}-link`} className="inline-flex w-fit items-center gap-1 text-xs text-accent no-underline">{x.link.label}<Icon name="chevron-right" size={12} /></Link>}
                  {x.link && !x.link.internal && !x.id.startsWith('vert-') && <a href={x.link.href} target="_blank" rel="noreferrer" className="inline-flex w-fit items-center gap-1 text-xs text-accent no-underline">{x.link.label}<Icon name="chevron-right" size={12} /></a>}
                  {/* VERT-1 (mockup F): the tip's action is a button */}
                  {x.link && !x.link.internal && x.id.startsWith('vert-') && (
                    <a href={x.link.href} target="_blank" rel="noreferrer" data-testid={`tip-${x.id}-link`} className="sx-btn sm mt-2 w-fit no-underline max-md:min-h-[44px]"><Icon name="external" size={16} />{x.link.label}</a>
                  )}
                  {x.action && <div className="mt-2"><TipButton action={x.action} testId={`tip-${x.id}-action`} onVertical={onVertical} /></div>}
                </div>
                {(x.kind === 'advice' || x.kind === 'ok') && (
                  <Button variant="ghost" size="sm" icon onClick={() => hide(x.id)} aria-label={t('tips.hide', { title: x.title })} className="flex-none max-md:h-11 max-md:w-11">
                    <Icon name="close" size={16} />
                  </Button>
                )}
              </div>
            )
          })}
        </section>
      ))}
    </div>
  )
}

/** Folded tips (52 px): urgent outputs as logos, then the tips mark. */
export function TipsRail({ tips, onOpen }: { tips: Tip[]; onOpen: () => void }) {
  const urgent = tips.filter((x) => x.kind === 'fix' || x.kind === 'input')
  return (
    <div data-testid="tips-rail" className="flex flex-col items-center gap-2 overflow-y-auto py-3">
      {urgent.map((x) => (
        <button key={x.id} type="button" onClick={onOpen} title={x.dest ? `${x.dest.name}: ${x.dest.note ?? ''}` : x.title}
          className="grid h-9 w-9 place-items-center rounded-md border border-warn/50">
          {x.dest ? <LogoSlot platform={x.dest.platform} /> : <span className="text-warn"><Icon name="state-warn" size={16} /></span>}
        </button>
      ))}
      <Button variant="ghost" icon onClick={onOpen} title={t('tips.open')} aria-label={t('tips.open')} className="text-accent"><Icon name="target" size={16} /></Button>
    </div>
  )
}
